<div class="authorization-list">

    <div class="header">
        <h3>Authorisations</h3>
        <button mat-icon-button color="primary" [disabled]="!patientId" (click)="openAdd()"
            matTooltip="Add an authorisation">
            <mat-icon>add</mat-icon>
        </button>
        <button mat-icon-button *ngIf="patientId" (click)="load()" matTooltip="Reload">
            <mat-icon>refresh</mat-icon>
        </button>
    </div>

    <!-- No patient row yet: the table needs a patientId to hang an authorisation on. -->
    <p class="hint" *ngIf="!patientId">
        Save the patient first — authorisations are stored against the patient record.
    </p>

    <p class="hint" *ngIf="patientId && loading">Loading authorisations…</p>
    <p class="hint warn-text" *ngIf="loadFailed">
        Authorisations could not be loaded. Nothing has been changed.
    </p>

    <!-- B4 — advisory. This banner disables nothing. -->
    <mat-card class="warning-banner-card" *ngIf="warnings.length">
        <mat-card-content>
            <div class="warning-banner">
                <mat-icon>warning</mat-icon>
                <div class="warning-content">
                    <strong>Authorisation coverage</strong>
                    <p class="warning-lead">
                        These are warnings only — nothing is blocked. Each one is a line the scheme
                        is likely to reject.
                    </p>
                    <ul>
                        <li *ngFor="let warning of warnings; trackBy: trackByWarning">{{ warning.message }}</li>
                    </ul>
                </div>
            </div>
        </mat-card-content>
    </mat-card>

    <p class="hint" *ngIf="patientId && !loading && !rows.length">
        No authorisations recorded.
    </p>

    <!-- Recorded authorisations, then numbers still stored on a patient or admission record,
         in the same shape. Editing a stored number records it as an authorisation. -->
    <div class="auth-row" *ngFor="let row of rows; trackBy: trackByRow">
        <div class="auth-main">
            <div class="auth-number">
                {{ row.authorization.authNumber }}
                <span class="chip status-{{ row.authorization.status }}" *ngIf="!row.legacy">{{ statusLabel(row.authorization.status) }}</span>
                <span class="chip linked" *ngIf="row.authorization.appointmentId">
                    <mat-icon inline="true">event</mat-icon>
                    {{ appointmentLabel(row.authorization.appointmentId) }}
                </span>
                <span class="chip alert" *ngIf="!row.legacy && warningsForAuthorization(row.authorization.id).length">
                    <mat-icon inline="true">warning</mat-icon>
                    {{ warningsForAuthorization(row.authorization.id).length }}
                </span>
            </div>
            <div class="auth-meta">
                <span *ngIf="row.authorization.schemeName">{{ row.authorization.schemeName }}</span>
                <span *ngIf="row.authorization.schemeNumber">member {{ row.authorization.schemeNumber }}</span>
                <span>{{ validityLabel(row.authorization) }}</span>
                <span *ngIf="row.authorization.authorisedCodes?.length">codes: {{ codesLabel(row.authorization) }}</span>
            </div>
            <div class="auth-notes" *ngIf="row.authorization.notes">{{ row.authorization.notes }}</div>
        </div>

        <div class="auth-actions">
            <mat-form-field appearance="outline" class="scope-field"
                *ngIf="!row.legacy && allowAppointmentScope && appointments.length">
                <mat-label>Booking</mat-label>
                <mat-select [value]="row.authorization.appointmentId"
                    (selectionChange)="scope(row.authorization, $event.value)">
                    <mat-option [value]="null">Not linked</mat-option>
                    <mat-option *ngFor="let option of appointments" [value]="option.id">
                        {{ option.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <button mat-icon-button color="primary" (click)="openEdit(row)" matTooltip="Edit">
                <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button color="warn" *ngIf="canRemove(row)" (click)="remove(row)" matTooltip="Remove">
                <mat-icon>delete</mat-icon>
            </button>
        </div>
    </div>

</div>

<ng-template #authorizationDialog>
    <h1 mat-dialog-title>{{ draft.id ? 'Edit authorisation' : 'New authorisation' }}</h1>
    <div mat-dialog-content>
        <div class="dialog-grid">

            <mat-form-field appearance="outline">
                <mat-label>Authorisation Number</mat-label>
                <input matInput [(ngModel)]="draft.authNumber" name="authNumber" required>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select [(ngModel)]="draft.status" name="status">
                    <mat-option *ngFor="let status of statuses" [value]="status.value">{{ status.label }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Scheme</mat-label>
                <input matInput [(ngModel)]="draft.schemeName" name="schemeName">
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Member Number</mat-label>
                <input matInput [(ngModel)]="draft.schemeNumber" name="schemeNumber">
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Valid From</mat-label>
                <input matInput [matDatepicker]="fromPicker" [(ngModel)]="draft.authorisedFrom" name="authorisedFrom">
                <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
                <mat-datepicker #fromPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Valid To</mat-label>
                <input matInput [matDatepicker]="toPicker" [(ngModel)]="draft.authorisedTo" name="authorisedTo">
                <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
                <mat-datepicker #toPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Authorised Days</mat-label>
                <input matInput type="number" min="1" [(ngModel)]="draft.authorisedDays" name="authorisedDays">
                <mat-hint>Schemes often quote days instead of an end date.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" *ngIf="allowAppointmentScope && appointments.length">
                <mat-label>Link to Booking</mat-label>
                <mat-select [(ngModel)]="draft.appointmentId" name="appointmentId">
                    <mat-option [value]="null">Not linked</mat-option>
                    <mat-option *ngFor="let option of appointments" [value]="option.id">
                        {{ option.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Notes</mat-label>
                <textarea matInput cdkTextareaAutosize="true" [(ngModel)]="draft.notes" name="notes"></textarea>
            </mat-form-field>

        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-raised-button color="primary" [disabled]="saving" (click)="save()">Save</button>
    </div>
</ng-template>
